import React from 'react';
import { motion } from 'framer-motion';

/* ============================================================
 * Reusable, theme-aware components for Persistent-style layouts.
 * All components use CSS variables — work in light & dark.
 * ============================================================ */

export function SectionHero({ eyebrow, title, subtitle, children, dark = true, backgroundImage, testId, topContent }) {
  const bg = dark
    ? { background: 'var(--dark-section-bg)', color: 'var(--dark-section-text)' }
    : { background: 'var(--background)', color: 'var(--text-primary)' };

  return (
    <section className="relative overflow-hidden" style={bg} data-testid={testId}>
      {backgroundImage && (
        <>
          <div
            className="absolute inset-0 opacity-20 bg-cover bg-center"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
          <div className="absolute inset-0" style={{ background: dark ? 'linear-gradient(120deg, #0B1120 0%, #0B1120cc 50%, #0B112099 100%)' : 'rgba(255,255,255,0.85)' }} />
        </>
      )}
      <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
        {topContent}
        {eyebrow && (
          <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-5" style={{ color: 'var(--primary-accent)' }}>
            {eyebrow}
          </div>
        )}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05]"
        >
          {title}
        </motion.h1>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 text-lg lg:text-xl max-w-3xl leading-relaxed"
            style={{ color: dark ? 'var(--dark-section-muted)' : 'var(--text-secondary)' }}
          >
            {subtitle}
          </motion.p>
        )}
        {children && <div className="mt-10">{children}</div>}
      </div>
    </section>
  );
}

export function StatsRow({ stats, dark = true }) {
  const bg = dark
    ? { background: 'var(--dark-section-alt)', color: 'var(--dark-section-text)' }
    : { background: 'var(--surface)', color: 'var(--text-primary)' };
  return (
    <section style={bg} className="border-y" data-testid="stats-row">
      <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="text-left"
              data-testid={`stat-${i}`}
            >
              <div className="font-heading text-4xl lg:text-5xl font-bold" style={{ color: 'var(--primary-accent)' }}>
                {s.value || s.metric}
              </div>
              <div className="mt-2 text-sm font-medium" style={{ color: dark ? 'var(--dark-section-muted)' : 'var(--text-secondary)' }}>
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SectionHeading({ eyebrow, title, subtitle, align = 'left', dark = false }) {
  const muted = dark ? 'var(--dark-section-muted)' : 'var(--text-secondary)';
  return (
    <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`}>
      {eyebrow && (
        <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: 'var(--primary-accent)' }}>
          {eyebrow}
        </div>
      )}
      <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight" style={{ color: dark ? 'var(--dark-section-text)' : 'var(--text-primary)' }}>
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-base lg:text-lg leading-relaxed" style={{ color: muted }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

export function ContentBlock({ eyebrow, title, body, bullets = [], imageRight = false, image, dark = false }) {
  return (
    <section className="py-16 lg:py-24" style={{ background: dark ? 'var(--dark-section-bg)' : 'var(--background)' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${imageRight ? '' : 'lg:[&>*:first-child]:order-2'}`}>
          <div>
            <SectionHeading eyebrow={eyebrow} title={title} subtitle={body} dark={dark} />
            {bullets.length > 0 && (
              <ul className="mt-8 space-y-3">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center" style={{ background: 'var(--primary-accent)' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <span className="text-base" style={{ color: dark ? 'var(--dark-section-text)' : 'var(--text-primary)' }}>
                      {typeof b === 'string' ? b : <><strong>{b.title}.</strong> <span style={{ color: dark ? 'var(--dark-section-muted)' : 'var(--text-secondary)' }}>{b.desc}</span></>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            {image || <InfographicPlaceholder dark={dark} />}
          </div>
        </div>
      </div>
    </section>
  );
}

export function InfographicPlaceholder({ dark = false }) {
  return (
    <div className="aspect-[4/3] rounded-2xl border flex items-center justify-center" style={{
      borderColor: dark ? 'var(--dark-section-border)' : 'var(--border)',
      background: dark ? 'var(--dark-section-alt)' : 'var(--surface)',
    }}>
      <ProcessFlowSVG />
    </div>
  );
}

/* ====== SVG INFOGRAPHICS ====== */

export function ProcessFlowSVG({ steps }) {
  const defaultSteps = ['Discovery', 'Design', 'Build', 'Launch', 'Scale'];
  const stepLabels = steps && steps.length > 0 ? steps : defaultSteps;
  return (
    <svg viewBox="0 0 600 400" className="w-full h-auto" data-testid="process-flow-svg">
      <defs>
        <linearGradient id="pf-line" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#EA580C" />
          <stop offset="100%" stopColor="#0D9488" />
        </linearGradient>
      </defs>
      {/* Connecting line */}
      <line x1="60" y1="200" x2="540" y2="200" stroke="url(#pf-line)" strokeWidth="2" strokeDasharray="6,4" />
      {stepLabels.slice(0, 5).map((label, i) => {
        const x = 60 + (i * 120);
        return (
          <g key={i}>
            <circle cx={x} cy="200" r="34" fill="#0B1120" stroke="#EA580C" strokeWidth="2" />
            <text x={x} y="207" fontSize="14" fill="#fff" textAnchor="middle" fontWeight="700">{i + 1}</text>
            <text x={x} y="260" fontSize="13" fill="currentColor" textAnchor="middle" fontWeight="600">{label}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function CapabilityWheelSVG({ items }) {
  const list = (items || []).slice(0, 8);
  const n = list.length || 1;
  return (
    <div className="relative w-full max-w-[560px] mx-auto aspect-square" data-testid="capability-wheel-svg">
      {list.map((label, i) => {
        const deg = (i / n) * 360 - 90;
        return (
          <div
            key={`line-${i}`}
            className="absolute"
            style={{ left: '50%', top: '50%', width: '36%', height: '2px', background: '#475569', transform: `rotate(${deg}deg)`, transformOrigin: '0 50%' }}
          />
        );
      })}
      {list.map((label, i) => {
        const rad = (i / n) * Math.PI * 2 - Math.PI / 2;
        const x = 50 + Math.cos(rad) * 36;
        const y = 50 + Math.sin(rad) * 36;
        return (
          <div
            key={`node-${i}`}
            className="absolute w-[92px] h-[92px] sm:w-[106px] sm:h-[106px] rounded-full bg-white flex items-center justify-center text-center px-2.5"
            style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)', border: '2px solid #EA580C' }}
            data-testid={`capability-node-${i}`}
          >
            <span className="text-[10px] sm:text-[11px] font-semibold leading-tight" style={{ color: '#0F172A' }}>{label}</span>
          </div>
        );
      })}
      <div
        className="absolute w-[120px] h-[120px] rounded-full flex flex-col items-center justify-center"
        style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', background: '#0B1120', border: '1px solid #334155' }}
      >
        <span className="text-sm font-bold text-white font-heading">Xceed</span>
        <span className="text-xs font-semibold" style={{ color: '#EA580C' }}>Agent</span>
      </div>
    </div>
  );
}

export function CTABanner({ title, subtitle, ctaText = 'Request for Proposal', ctaHref = '/start-project', secondaryText, secondaryHref }) {
  return (
    <section className="dark-section" data-testid="cta-banner">
      <div className="max-w-5xl mx-auto px-6 py-20 lg:py-24 text-center">
        <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight">{title}</h2>
        {subtitle && <p className="mt-5 text-lg max-w-2xl mx-auto" style={{ color: 'var(--dark-section-muted)' }}>{subtitle}</p>}
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <a href={ctaHref} data-testid="cta-banner-primary">
            <button className="h-12 px-8 rounded-md font-semibold hover-lift transition-all" style={{ background: 'var(--primary-accent)', color: '#fff' }}>
              {ctaText} →
            </button>
          </a>
          {secondaryText && (
            <a href={secondaryHref} data-testid="cta-banner-secondary">
              <button className="h-12 px-8 rounded-md font-semibold border" style={{ borderColor: 'var(--dark-section-border)', color: '#fff' }}>
                {secondaryText}
              </button>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

export function IndustryGrid({ items, basePath = '/industries' }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((it) => (
        <a key={it.id} href={`${basePath}/${it.id}`} className="group p-6 rounded-xl border bg-white hover:shadow-elevated transition-all hover-lift" style={{ borderColor: 'var(--border)' }} data-testid={`industry-${it.id}`}>
          <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: 'var(--primary-accent)' }}>{it.shortName}</div>
          <div className="font-heading text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{it.name}</div>
          <div className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{(it.summary || it.headline || '').slice(0, 100)}…</div>
          <div className="mt-4 text-sm font-semibold flex items-center gap-1.5" style={{ color: 'var(--primary-accent)' }}>
            Explore <span className="transition-transform group-hover:translate-x-0.5">→</span>
          </div>
        </a>
      ))}
    </div>
  );
}

export function FAQAccordion({ items }) {
  const [openIdx, setOpenIdx] = React.useState(0);
  return (
    <div className="divide-y" style={{ borderColor: 'var(--border)' }} data-testid="faq-accordion">
      {items.map((item, i) => (
        <div key={i} className="py-5">
          <button
            onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
            className="w-full text-left flex items-start justify-between gap-4"
            data-testid={`faq-q-${i}`}
          >
            <span className="font-heading text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{item.q}</span>
            <span className="flex-shrink-0 mt-1 w-6 h-6 rounded-full border flex items-center justify-center" style={{ borderColor: 'var(--border-strong)', color: 'var(--text-primary)' }}>
              {openIdx === i ? '−' : '+'}
            </span>
          </button>
          {openIdx === i && (
            <div className="mt-3 text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {item.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
